/**
 * AdaptiveMicLevelMeter — turns a per-frame linear RMS amplitude into a 0..1
 * display level for an "honest" microphone waveform.
 *
 * Why this exists: AnalyserNode.getByteFrequencyData is a dB-domain measure
 * floored at -100 dB. Using its bin-average as "loudness" makes a quiet room
 * render as a tall, dancing waveform (a -65 dBFS noise floor maps to ~half
 * scale), and speech is only marginally taller. This meter instead works in
 * linear RMS -> dBFS and renders signal ABOVE a per-microphone ADAPTIVE noise
 * floor, so that:
 *   - a quiet room reads ~0 on ANY mic (the floor is measured, not assumed), and
 *   - speech (typically 20-40 dB above the floor) reads clearly.
 *
 * The class is pure and synchronous (no Web Audio dependency) so it is fully
 * unit-testable. The Web Audio plumbing lives in {@link MicLevelMeter}.
 */

export interface AdaptiveMeterConfig {
  /** dB above the noise floor before the bar leaves zero (gate keeps silence flat). */
  gateDb: number;
  /** dB span above the gate that maps to full bar height. Voice sits ~20-40 dB over the floor. */
  spanDb: number;
  /** Floor follower: fraction it moves DOWN toward a quieter level each frame (fast attack to quiet). */
  floorFall: number;
  /** Floor follower: fraction it moves UP toward a louder level each frame (slow, so speech can't poison it). */
  floorRise: number;
  /** Output smoothing retained per frame when the level drops (slow release; fast attack is instant). */
  release: number;
  /** dBFS value used for true digital silence (rms === 0) to avoid log(0). */
  silenceFloorDb: number;
}

export const DEFAULT_METER_CONFIG: AdaptiveMeterConfig = {
  gateDb: 8,
  spanDb: 26,
  floorFall: 0.5,
  floorRise: 0.0008,
  release: 0.85,
  silenceFloorDb: -100,
};

/** Root-mean-square (linear amplitude, ~0..1) of a time-domain sample buffer. Pure helper. */
export function rmsFromTimeDomain(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / samples.length);
}

export class AdaptiveMicLevelMeter {
  private readonly config: AdaptiveMeterConfig;
  private floorDb: number | null = null;
  private shown = 0;

  constructor(config: Partial<AdaptiveMeterConfig> = {}) {
    this.config = { ...DEFAULT_METER_CONFIG, ...config };
  }

  /** Reset all state — call when a new stream/microphone starts. */
  reset(): void {
    this.floorDb = null;
    this.shown = 0;
  }

  /** Current adaptive noise-floor estimate in dBFS (null until the first sample). */
  getFloorDb(): number | null {
    return this.floorDb;
  }

  /**
   * Feed one frame's linear RMS amplitude; returns the smoothed 0..1 display level.
   */
  update(rms: number): number {
    const { gateDb, spanDb, floorFall, floorRise, release, silenceFloorDb } = this.config;

    const db = rms > 0 ? 20 * Math.log10(rms) : silenceFloorDb;

    // Adaptive noise floor: snap DOWN toward quiet quickly, creep UP slowly so a
    // user already talking when the meter mounts can't drag the floor up and
    // gate their own voice out. Seed on the first frame to avoid ramping from -inf.
    //
    // Digital silence (rms === 0: muted/disabled/dead track) is gated out but
    // must never adapt the floor — zeros mean "no signal", not "an infinitely
    // quiet room". Otherwise the floor slams to silenceFloorDb during a mute
    // and, on recovery, ambient noise reads ~40 dB above it, pegging the meter
    // at full scale for the minutes floorRise needs to climb back.
    if (rms > 0) {
      if (this.floorDb === null) {
        this.floorDb = db;
      } else if (db < this.floorDb) {
        this.floorDb += (db - this.floorDb) * floorFall;
      } else {
        this.floorDb += (db - this.floorDb) * floorRise;
      }
    }

    if (this.floorDb === null) {
      // Only digital silence seen so far — nothing to show yet.
      this.shown = this.shown * release;
      return this.shown;
    }

    const aboveGate = db - (this.floorDb + gateDb);
    const target = Math.max(0, Math.min(1, aboveGate / spanDb));

    // Fast attack (jump up immediately on speech), slow release (ease back down).
    this.shown = target > this.shown ? target : this.shown * release + target * (1 - release);

    return this.shown;
  }
}
